<template>
  <BModal
    :model-value="modelValue"
    :title="title"
    class="login-dialog"
    body-class="p-0"
    no-footer
    centered
    size="xl"
    @update:model-value="$emit('update:modelValue', $event)"
    @hidden="onHidden"
  >
    <div tabindex="-1" class="dialog-body">
      <div class="slides" :class="{ 'slides--shifted': view !== 'register' }">

        <div class="slide p-5" :inert="view !== 'register'">
          <div class="d-flex h-100 justify-content-between flex-column">
            <form @submit.prevent="submitRegister">
              <BFormGroup label="Name" floating class="mb-3" :state="hasFieldError('name') ? false : null" :invalid-feedback="getFieldMessages('name').join('. ')">
                <BFormInput v-model="name" type="text" required autocomplete="name" placeholder="Name" />
              </BFormGroup>
              <BFormGroup label="Email" floating class="mb-3" :state="hasFieldError('email') ? false : null" :invalid-feedback="getFieldMessages('email').join('. ')">
                <BFormInput v-model="email" type="email" required autocomplete="email" placeholder="Email" />
              </BFormGroup>
              <BFormGroup label="Password" floating class="mb-3" :state="hasFieldError('password') ? false : null" :invalid-feedback="getFieldMessages('password').join('. ')">
                <BFormInput v-model="registerPassword" :type="showRegisterPassword ? 'text' : 'password'" required autocomplete="new-password" placeholder="Password" class="pe-5" />
                <button type="button" class="password-toggle" tabindex="-1" @click="showRegisterPassword = !showRegisterPassword">
                  <Icon :icon="showRegisterPassword ? 'bi:eye-slash' : 'bi:eye'" />
                </button>
              </BFormGroup>
              <PasswordStrengthMeter :password="registerPassword" />
              <BFormGroup label="Confirm password" floating class="mb-3" :state="hasFieldError('passwordConfirmation') ? false : null" :invalid-feedback="getFieldMessages('passwordConfirmation').join('. ')">
                <BFormInput v-model="passwordConfirmation" :type="showConfirmPassword ? 'text' : 'password'" required autocomplete="new-password" placeholder="Confirm password" class="pe-5" />
                <button type="button" class="password-toggle" tabindex="-1" @click="showConfirmPassword = !showConfirmPassword">
                  <Icon :icon="showConfirmPassword ? 'bi:eye-slash' : 'bi:eye'" />
                </button>
              </BFormGroup>
              <BFormCheckbox v-model="remember" class="mb-3">Keep me signed in</BFormCheckbox>
              <div v-if="siteKey" ref="turnstileContainer" class="mb-3"></div>
              <div class="d-grid mb-3">
                <BButton type="submit" variant="primary" :disabled="loading">
                  {{ loading ? 'Creating account…' : 'Create account' }}
                </BButton>
              </div>
            </form>
            <BButton class="align-self-end" type="button" variant="secondary" @click="switchTo('form')">
              Back to sign in <Icon icon="bi:arrow-right" />
            </BButton>
          </div>
        </div>

        <div class="slide-image" aria-hidden="true">
          <img src="@/assets/signup-login.png" alt="" />
        </div>

        <div class="slide p-5" :inert="view !== 'form' && view !== 'totp'">
          <div class="d-flex h-100 justify-content-between flex-column">
            <form v-if="view !== 'totp'" class="d-flex h-100 flex-column" @submit.prevent="submitLogin">
              <BFormGroup label="Email" floating class="mb-3" :state="hasFieldError('email') ? false : null" :invalid-feedback="getFieldMessages('email').join('. ')">
                <BFormInput v-model="email" type="email" required autocomplete="email" placeholder="Email" />
              </BFormGroup>
              <BFormGroup label="Password" floating class="mb-3" :state="hasFieldError('password') ? false : null" :invalid-feedback="getFieldMessages('password').join('. ')">
                <BFormInput v-model="loginPassword" :type="showLoginPassword ? 'text' : 'password'" required autocomplete="current-password" placeholder="Password" class="pe-5" />
                <button type="button" class="password-toggle" tabindex="-1" @click="showLoginPassword = !showLoginPassword">
                  <Icon :icon="showLoginPassword ? 'bi:eye-slash' : 'bi:eye'" />
                </button>
              </BFormGroup>
              <div class="text-end mb-3">
                <a href="#" class="text-muted small" @click.prevent>Forgot password?</a>
              </div>
              <BFormCheckbox v-model="remember" class="mb-3">Keep me signed in</BFormCheckbox>
              <div class="d-grid mb-3">
                <BButton type="submit" variant="primary" :disabled="loading">
                  {{ loading ? 'Signing in…' : 'Sign in' }}
                </BButton>
              </div>
            </form>
            <form v-else class="d-flex h-100" @submit.prevent="submitTotp">
              <p class="text-muted mb-3">Enter the code from your authenticator app.</p>
              <BFormGroup label="Authentication code" floating class="mb-3">
                <BFormInput v-model="totpCode" type="text" required autocomplete="one-time-code" inputmode="numeric" pattern="[0-9]*" placeholder="000000" />
              </BFormGroup>
              <div class="d-grid">
                <BButton type="submit" variant="primary" :disabled="loading">
                  {{ loading ? 'Verifying…' : 'Verify' }}
                </BButton>
              </div>
            </form>
            <BButton class="align-self-start" v-if="canRegister" type="button" variant="secondary" @click="switchTo('register')">
              <Icon icon="bi:arrow-left" /> Create account
            </BButton>
          </div>
        </div>
      </div>
    </div>
  </BModal>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { BModal, BFormGroup, BFormInput, BFormCheckbox, BButton, useToast } from 'bootstrap-vue-next'
import { Icon } from '@iconify/vue'
import { login, register, verifyTotp, canRegister } from '@/stores/auth'
import { config } from '@/stores/config'
import { calculatePasswordStrength } from '@/composables/passwordStrength'
import { useFormErrors } from '@/composables/useFormErrors'
import { BackendError } from '@/services/Backend'
import PasswordStrengthMeter from '@/components/PasswordStrengthMeter.vue'

defineProps<{ modelValue: boolean }>()
const emit = defineEmits<{ (e: 'update:modelValue', value: boolean): void }>()

type View = 'form' | 'register' | 'totp'

const toast = useToast()
const { t } = useI18n()
const { getFieldMessages, hasFieldError, setErrors, addFieldErrors, clearErrors, clearField } = useFormErrors({
    messages: {
        'name.NO_MATCH': t('auth.nameInvalid'),
    },
})

const view = ref<View>('form')
const loading = ref(false)

const email = ref('')
const loginPassword = ref('')
const registerPassword = ref('')
const passwordConfirmation = ref('')
const name = ref('')
const remember = ref(false)
const totpCode = ref('')
const showLoginPassword = ref(false)
const showRegisterPassword = ref(false)
const showConfirmPassword = ref(false)

const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined
const turnstileContainer = ref<HTMLElement | null>(null)
const turnstileToken = ref('')
let widgetId: string | undefined

watch(view, async (v) => {
    if (v === 'register' && siteKey) {
        await (window as any).__turnstileReady
        await nextTick()
        if (turnstileContainer.value && widgetId === undefined) {
            widgetId = (window as any).turnstile.render(turnstileContainer.value, {
                sitekey: siteKey,
                callback: (token: string) => { turnstileToken.value = token },
                'expired-callback': () => { turnstileToken.value = '' },
            })
        }
    }
})

watch(name, () => clearField('name'))
watch(email, () => clearField('email'))
watch(loginPassword, () => clearField('password'))
watch(registerPassword, () => clearField('password'))
watch(passwordConfirmation, () => clearField('passwordConfirmation'))

const title = computed(() => {
    if (view.value === 'register') return 'Create account'
    if (view.value === 'totp') return 'Two-factor authentication'
    return 'Sign in'
})

function showErrorToast(message: string) {
    toast.create({
        body: message,
        variant: 'danger',
        progressProps: {
          variant: 'warning',
        },
        pos: 'top-end',
        modelValue: message.length * 300,
        solid: true,
    })
}

function slugify(message: string): string {
    return message.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-')
}

function handleError(e: unknown) {
    if (e instanceof BackendError) {
        setErrors(e.errors)
        if (e.message) {
            const key = `auth.error.${slugify(e.message)}`
            const translated = t(key)
            showErrorToast(translated === key ? e.message : translated)
        }
    } else {
        showErrorToast('An unexpected error occurred')
    }
}

function switchTo(target: View) {
    clearErrors()
    view.value = target
}

function close() {
    emit('update:modelValue', false)
}

function onHidden() {
    view.value = 'form'
    clearErrors()
    loginPassword.value = ''
    registerPassword.value = ''
    passwordConfirmation.value = ''
    totpCode.value = ''
    if (widgetId !== undefined) {
        (window as any).turnstile?.reset(widgetId)
        turnstileToken.value = ''
    }
}

async function submitLogin() {
    clearErrors()
    loading.value = true
    try {
        const result = await login(email.value, loginPassword.value, remember.value)
        if (result === 'totp') {
            view.value = 'totp'
        } else {
            close()
        }
    } catch (e) {
        handleError(e)
    } finally {
        loading.value = false
    }
}

async function submitRegister() {
    clearErrors()

    const minScore = config.passwordStrengthMinScore.value
    if (minScore > 0 && calculatePasswordStrength(registerPassword.value) < minScore) {
        addFieldErrors('password', [{ key: 'PASSWORD_TOO_WEAK', message: 'Password is too weak' }])
        return
    }

    loading.value = true
    try {
        await register(name.value, email.value, registerPassword.value, passwordConfirmation.value, remember.value, turnstileToken.value)
        close()
    } catch (e) {
        handleError(e)
        if (widgetId !== undefined) {
            (window as any).turnstile?.reset(widgetId)
            turnstileToken.value = ''
        }
    } finally {
        loading.value = false
    }
}

async function submitTotp() {
    loading.value = true
    try {
        await verifyTotp(totpCode.value)
        close()
    } catch (e) {
        handleError(e)
    } finally {
        loading.value = false
    }
}
</script>

<style scoped>
.dialog-body {
  overflow: hidden;
  outline: none;
}

.slides {
  display: flex;
  width: 200%;
  transition: transform 0.3s ease;

  &.slides--shifted {
    transform: translateX(-50%);
  }
}

.slide {
  width: 50%;
  min-width: 50%;
}

.slide-image {
  display: none;
}

@media (min-width: 992px) {
  .slides {
    width: calc(550% / 3);

    &.slides--shifted {
      transform: translateX(calc(-500% / 11));
    }
  }

  .slide {
    width: calc(300% / 11);
    min-width: calc(300% / 11);
  }

  .slide-image {
    display: block;
    width: calc(500% / 11);
    min-width: calc(500% / 11);
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
  }
}

.password-toggle {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 0.25rem;
  color: #6c757d;
  cursor: pointer;
  line-height: 0;
  z-index: 5;
}
</style>
